import { AlertTriangle, CheckCircle2, Copy, Download } from "lucide-react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useClipboard } from "@/hooks/use-clipboard"
import { downloadBlob, toCsvBlob } from "@/lib/csv"
import { cn } from "@/lib/utils"
import type { ImportFailure, ImportResult } from "@/types/candidates"

interface ImportResultPanelProps {
  result: ImportResult
  /** Brand display name — surfaced in the summary copy. */
  brandName: string
  /** Renders the toolbar pair: "Upload another" + "View candidates". */
  actions?: React.ReactNode
}

/**
 * Bulk-import result surface — renders the partial-success summary +
 * per-row failure table + clipboard/download helpers.
 *
 * **Display logic:**
 *   - `failures.length === 0` → all-success branch (green check, full
 *     summary, no failure table).
 *   - `created_count === 0`   → all-failure branch (amber warning,
 *     "no candidates imported" copy, failure table prominent).
 *   - else                    → partial-success branch (amber warning,
 *     both panels visible, summary contextualises the split).
 *
 * The failures table is the primary user surface — UX Spec §Result
 * specifies it as a `Table` with `Row #`, `Handle`, `Reason` columns
 * and a sticky download CTA. UX Spec also called for a side-by-side
 * "Imported (X)" list, but FE chose to collapse that to a count —
 * listing every imported handle adds noise the user doesn't act on,
 * and OKS-17's index page is the canonical view of successful
 * imports. UX Lead reviewed in Phase 3 and accepted on the basis
 * that "View candidates" satisfies the success-verification need.
 *
 * **Localised reason copy:** every `failure.reason_code` resolves
 * via the per-code static switch in `resolveReason()` (one
 * call per enum value, one per failure_reasons child key).
 * The `unknown` discriminant falls back to `failure.reason_message`
 * (server-localised) — defence in depth against a backend code we
 * haven't seen yet.
 *
 * Locked QA testid contract (FE TRD §testid):
 *   - `import-result` (wrapper)
 *   - `import-summary` ("X created, Y skipped" line)
 *   - `import-failures-table` (per-row failures)
 *   - `import-failure-row-{n}` (each failure row, n = original CSV row)
 *   - `import-failures-copy` (clipboard CTA)
 *   - `import-failures-download` (download-as-CSV CTA)
 *
 * Failure-row stagger fade (OKS-128 — see UX Spec §Micro-Interactions):
 *   Each `<TableRow>` in the failures map carries
 *   `style={{ animationDelay }}` driving a one-shot CSS keyframe.
 *   Rows 0-9 get 0/50/…/450ms + the `motion-safe:animate-in fade-in
 *   slide-in-from-bottom-2` class set; rows 10+ get `0ms` and DROP
 *   the animation classes entirely (instant visible at t=0). One-shot
 *   per mount via stable React keys — no `useState`/`useEffect`.
 */
export function ImportResultPanel({
  result,
  brandName,
  actions,
}: ImportResultPanelProps) {
  const { t } = useTranslation()
  const [, copy] = useClipboard()

  const { created_count, skipped_count, total_rows, failures } = result
  const isFullSuccess = skipped_count === 0 && failures.length === 0
  const isFullFailure = created_count === 0 && skipped_count > 0

  // i18n keys are looked up via static `t("…")` calls so the
  // inertia_i18n health-checker can statically tie keys to consumers
  // — dynamic template-literal lookups would surface as false-
  // positive "unused YAML key" failures.
  const interp = {
    created: created_count,
    skipped: skipped_count,
    total: total_rows,
    count: created_count,
    brand: brandName,
  }
  const titleText = isFullSuccess
    ? t("candidates.bulk.result.title_full", interp)
    : isFullFailure
      ? t("candidates.bulk.result.title_zero", interp)
      : t("candidates.bulk.result.title_partial", interp)
  const summaryText = isFullSuccess
    ? t("candidates.bulk.result.summary_full", interp)
    : isFullFailure
      ? t("candidates.bulk.result.summary_zero", interp)
      : t("candidates.bulk.result.summary_partial", interp)

  return (
    <section
      className="flex flex-col gap-6"
      data-testid="import-result"
      role="region"
      aria-labelledby="import-result-title"
    >
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          {/*
           * Status icon paired with text per UX Spec §Accessibility —
           * colour is never the sole indicator. The icon is decorative;
           * the title carries the meaning for SR users.
           */}
          {isFullSuccess ? (
            <CheckCircle2
              className="text-emerald-600"
              aria-hidden="true"
              size={24}
            />
          ) : (
            <AlertTriangle
              className="text-amber-600"
              aria-hidden="true"
              size={24}
            />
          )}
          <h2
            id="import-result-title"
            className="text-foreground text-xl font-semibold tracking-tight"
          >
            {titleText}
          </h2>
        </div>
        <p
          className="text-muted-foreground text-sm"
          data-testid="import-summary"
        >
          {summaryText}
        </p>
      </header>

      {failures.length > 0 && (
        <FailuresPanel
          failures={failures}
          onCopy={() => {
            void (async () => {
              const text = failuresAsClipboardText(failures, t)
              const ok = await copy(text)
              if (ok) toast.success(t("candidates.toast.copy_success"))
              else toast.error(t("candidates.toast.copy_failure"))
            })()
          }}
          onDownload={() => {
            const blob = toCsvBlob(
              [
                t("candidates.bulk.result.col_row"),
                t("candidates.bulk.result.col_handle"),
                t("candidates.bulk.result.col_reason"),
              ],
              failures.map((failure) => [
                String(failure.row),
                failure.tiktok_handle ?? "",
                resolveReason(failure, t),
              ]),
            )
            downloadBlob(blob, "candidate-import-skipped-rows.csv")
          }}
        />
      )}

      {actions && (
        <footer className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          {actions}
        </footer>
      )}
    </section>
  )
}

interface FailuresPanelProps {
  failures: ImportFailure[]
  onCopy: () => void
  onDownload: () => void
}

function FailuresPanel({ failures, onCopy, onDownload }: FailuresPanelProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
        <h3 className="text-foreground text-base font-medium">
          {t("candidates.bulk.result.skipped_heading", {
            count: failures.length,
          })}
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCopy}
            data-testid="import-failures-copy"
            className="max-sm:h-11"
          >
            <Copy className="size-4" aria-hidden="true" />
            {t("candidates.bulk.result.copy_failures")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onDownload}
            data-testid="import-failures-download"
            className="max-sm:h-11"
          >
            <Download className="size-4" aria-hidden="true" />
            {t("candidates.bulk.result.download_failures")}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <Table data-testid="import-failures-table">
          <TableCaption className="sr-only">
            {t("candidates.bulk.result.skipped_heading", {
              count: failures.length,
            })}
          </TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col" className="w-20">
                {t("candidates.bulk.result.col_row")}
              </TableHead>
              <TableHead scope="col">
                {t("candidates.bulk.result.col_handle")}
              </TableHead>
              <TableHead scope="col">
                {t("candidates.bulk.result.col_reason")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {failures.map((failure, i) => {
              // UX Spec §Micro-Interactions: 50ms stagger fade for
              // the first 10 rows; rows 11+ render with NO animation
              // classes at all (instant visible at t=0). Vitest reads
              // back the inline `style.animationDelay` directly (jsdom
              // preserves React-set inline styles); no separate data
              // attribute needed.
              const shouldAnimate = i < 10
              const animationDelay = shouldAnimate ? `${i * 50}ms` : "0ms"
              return (
                <TableRow
                  key={`${failure.row}-${failure.reason_code}`}
                  data-testid={`import-failure-row-${failure.row}`}
                  data-row={failure.row}
                  data-reason-code={failure.reason_code}
                  style={{ animationDelay }}
                  className={cn(
                    shouldAnimate &&
                      "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2",
                  )}
                >
                  <TableCell className="text-muted-foreground tabular-nums">
                    {failure.row}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "font-medium",
                      failure.tiktok_handle === null && "text-muted-foreground",
                    )}
                  >
                    {failure.tiktok_handle ?? "—"}
                  </TableCell>
                  <TableCell className="text-sm leading-snug">
                    {resolveReason(failure, t)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

/**
 * Localise a failure reason. Prefers the i18n key keyed off
 * `reason_code`; falls back to the backend-provided `reason_message`
 * on the `unknown` discriminant.
 *
 * **Why a static switch (not a dynamic template-literal):** the
 * `inertia_i18n` health-checker statically traces translation calls
 * to verify every YAML key has at least one consumer. A dynamic
 * lookup keyed off a runtime variable is opaque to the checker —
 * it can't tie the runtime key to the YAML, surfacing every reason
 * key as "unused" + the parameter literally as "missing key with
 * the brace placeholder". The switch is uglier but each call is
 * statically visible.
 */
function resolveReason(
  failure: ImportFailure,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const interp = {
    field: failure.field ?? "",
    first_row: failure.first_row ?? "",
  }
  switch (failure.reason_code) {
    case "missing_required":
      return t("candidates.failure_reasons.missing_required", interp)
    case "malformed_handle":
      return t("candidates.failure_reasons.malformed_handle", interp)
    case "followers_not_numeric":
      return t("candidates.failure_reasons.followers_not_numeric", interp)
    case "duplicate_in_file":
      return t("candidates.failure_reasons.duplicate_in_file", interp)
    case "duplicate_in_brand":
      return t("candidates.failure_reasons.duplicate_in_brand", interp)
    case "row_too_long":
      return t("candidates.failure_reasons.row_too_long", interp)
    case "unknown":
      // Backend-localised fallback — defence in depth against a code
      // we haven't seen yet (also surfaces server-side wording on
      // truly unrecognised failures).
      return failure.reason_message
  }
}

/**
 * Plain-text rendering of the failure list for the clipboard. Tab-
 * separated so it pastes cleanly into a spreadsheet column.
 */
function failuresAsClipboardText(
  failures: ImportFailure[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const header = [
    t("candidates.bulk.result.col_row"),
    t("candidates.bulk.result.col_handle"),
    t("candidates.bulk.result.col_reason"),
  ].join("\t")
  const lines = failures.map((failure) =>
    [
      String(failure.row),
      failure.tiktok_handle ?? "",
      resolveReason(failure, t),
    ].join("\t"),
  )
  return [header, ...lines].join("\n")
}
